import { z } from "zod";

// Base image schema
const ImageSchema = z.object({
  id: z.string(),
  src: z.string(),
  alt: z.string(),
  href: z.string().optional(),
  width: z.number().optional(),
  height: z.number().optional(),
  priority: z.boolean().optional(),
  caption: z.string().optional(),
  tags: z.array(z.string()).optional(),
});

// Brand-specific schema
const BrandSchema = ImageSchema.extend({
  tags: z.array(z.string()).default([]),
});

// Testimonial-specific schema
const TestimonialSchema = ImageSchema.extend({
  rating: z.number().min(1).max(5),
  location: z.string(),
  author: z.string(),
  role: z.string(),
  quote: z.string(),
  tags: z.array(z.string()).default([]),
});

// Gallery-specific schema
const GallerySchema = ImageSchema.extend({
  title: z.string(),
  genre: z.string(),
  rating: z.number().min(0).max(5),
  tags: z.array(z.string()).default([]),
});

// Hero-specific schema
const HeroSchema = ImageSchema.extend({
  tags: z.array(z.string()).default([]),
});

// Collection schemas
const BrandCollectionSchema = z.object({
  items: z.array(BrandSchema),
});

const TestimonialCollectionSchema = z.object({
  items: z.array(TestimonialSchema),
});

const GalleryCollectionSchema = z.object({
  items: z.array(GallerySchema),
});

const HeroCollectionSchema = z.object({
  items: z.array(HeroSchema),
});

// Type exports
export type Brand = z.infer<typeof BrandSchema>;
export type Testimonial = z.infer<typeof TestimonialSchema>;
export type Gallery = z.infer<typeof GallerySchema>;
export type Hero = z.infer<typeof HeroSchema>;

export type BrandCollection = z.infer<typeof BrandCollectionSchema>;
export type TestimonialCollection = z.infer<typeof TestimonialCollectionSchema>;
export type GalleryCollection = z.infer<typeof GalleryCollectionSchema>;
export type HeroCollection = z.infer<typeof HeroCollectionSchema>;

// Content loaders with validation
export async function loadBrands(): Promise<BrandCollection> {
  try {
    const brandsData = await import("@/content/images/brands.json");
    return BrandCollectionSchema.parse(brandsData.default);
  } catch (error) {
    console.error("Failed to load or validate brands data:", error);
    return { items: [] };
  }
}

export async function loadTestimonials(): Promise<TestimonialCollection> {
  try {
    const testimonialsData = await import("@/content/images/testimonials.json");
    return TestimonialCollectionSchema.parse(testimonialsData.default);
  } catch (error) {
    console.error("Failed to load or validate testimonials data:", error);
    return { items: [] };
  }
}

export async function loadGallery(): Promise<GalleryCollection> {
  try {
    const galleryData = await import("@/content/images/gallery.json");
    return GalleryCollectionSchema.parse(galleryData.default);
  } catch (error) {
    console.error("Failed to load or validate gallery data:", error);
    return { items: [] };
  }
}

export async function loadHero(): Promise<HeroCollection> {
  try {
    const heroData = await import("@/content/images/hero.json");
    return HeroCollectionSchema.parse(heroData.default);
  } catch (error) {
    console.error("Failed to load or validate hero data:", error);
    return { items: [] };
  }
}

// Utility functions
export function filterByTags<T extends { tags?: string[] }>(items: T[], tags: string[]): T[] {
  return items.filter(item => 
    item.tags?.some(tag => tags.includes(tag)) ?? false
  );
}

export function sortByRating<T extends { rating?: number }>(items: T[]): T[] {
  return [...items].sort((a, b) => (b.rating ?? 0) - (a.rating ?? 0));
}